<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Multi Packs"
    :items="allMultiPacks || []"
    :headers="headers"
    item-key="Multi_Pack_SK"
    selection-state-name="selectedMultiPackSks"
    :loading="allMultiPacksLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Multi Pack"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Multi_Pack_Name" label="Multi Pack Name" autofocus></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Multi Packs'"
    item-key="Multi_Pack_SK"
    display-field="Multi_Pack_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Multi Pack"
    :item-name="itemToDelete?.Multi_Pack_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_MULTI_PACKS from '@/graphql/MultiPack/allMultiPacks.gql'
import ADD_MULTI_PACK from '@/graphql/MultiPack/addMultiPack.gql'
import UPDATE_MULTI_PACK from '@/graphql/MultiPack/updateMultiPack.gql'
import REMOVE_MULTI_PACK from '@/graphql/MultiPack/removeMultiPack.gql'
import MERGE_MULTI_PACK from '@/graphql/MultiPack/mergeMultiPack.gql'

const store = useStore()

const {
  result: allMultiPacksResult,
  loading: allMultiPacksLoading,
} = useQuery(ALL_MULTI_PACKS)
const allMultiPacks = computed(() => allMultiPacksResult.value?.allMultiPacks)

const headers = [
  { title: '#', key: 'Multi_Pack_SK', filterType: 'number', align: 'center' },
  { title: 'Name', key: 'Multi_Pack_Name', filterType: 'text', align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Multi Pack' : 'Edit Multi Pack')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allMultiPacks.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addMultiPack } = useMutation(ADD_MULTI_PACK,
  () => ({
    update: (cache, { data: { addMultiPack } }) => {
      const { allMultiPacks } = cache.readQuery({ query: ALL_MULTI_PACKS })
      const newData = { allMultiPacks: [...allMultiPacks, addMultiPack] }
      cache.writeQuery({ query: ALL_MULTI_PACKS, data: newData })
    }
  })
)

const { mutate: updateMultiPack } = useMutation(UPDATE_MULTI_PACK)

const save = () => {
  if (editedIndex.value > -1) {
    updateMultiPack({
      UpdateMultiPackInput: {
        Multi_Pack_SK: editedItem.value.Multi_Pack_SK,
        Multi_Pack_Name: editedItem.value.Multi_Pack_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    addMultiPack({
      AddMultiPackInput: {
        Multi_Pack_Name: editedItem.value.Multi_Pack_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeMultiPack } = useMutation(REMOVE_MULTI_PACK)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeMultiPack({
    Multi_Pack_SK: item.Multi_Pack_SK
  }, {
    update: (cache, { data: { removeMultiPack } }) => {
      const { allMultiPacks } = cache.readQuery({ query: ALL_MULTI_PACKS })
      const newData = { allMultiPacks: allMultiPacks.filter((m) => m.Multi_Pack_SK !== removeMultiPack) }
      cache.writeQuery({ query: ALL_MULTI_PACKS, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Multi_Pack_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeMultiPack } = useMutation(MERGE_MULTI_PACK)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeMultiPack({
    MergeMultiPackInput: {
      survivor_Multi_Pack_SK: mergeData.survivor,
      duplicate_Multi_Pack_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allMultiPacks } = cache.readQuery({ query: ALL_MULTI_PACKS })
      const newData = {
        allMultiPacks: allMultiPacks.filter(multiPack => !mergeData.duplicates.includes(multiPack.Multi_Pack_SK))
      }
      cache.writeQuery({ query: ALL_MULTI_PACKS, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} multi packs`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
